import React from 'react';

import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import classNames from 'classnames';

const ARROW_PLACEMENT = {
  top: 'top',
  bottom: 'bottom',
} as const;

export type ArrowPlacement =
  (typeof ARROW_PLACEMENT)[keyof typeof ARROW_PLACEMENT];

interface IProps {
  anchorElement: React.RefObject<HTMLElement>;
  open: boolean;
  message: React.ReactNode;
  arrowPlacement?: ArrowPlacement;
  onDismiss: () => void;
}

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 14;
const ARROW_MARGIN = 8;

const useStableAnchorNode = (anchorElement: React.RefObject<HTMLElement>) => {
  const [stableAnchorNode, setStableAnchorNode] = React.useState<HTMLElement>();

  React.useLayoutEffect(() => {
    anchorElement.current && setStableAnchorNode(anchorElement.current);
  }, [anchorElement]);

  return stableAnchorNode;
};

const useAnchorDescribedBy = (
  anchorElement: React.RefObject<HTMLElement>,
  descriptionId: string,
  open: boolean,
) => {
  React.useEffect(() => {
    const anchorNode = anchorElement.current;

    if (!anchorNode || !open) return;

    anchorNode.setAttribute('aria-describedby', descriptionId);

    return () => anchorNode.removeAttribute('aria-describedby');
  }, [anchorElement, descriptionId, open]);
};

const useAnchorPressDismiss = (
  anchorElement: React.RefObject<HTMLElement>,
  open: boolean,
  onDismiss: () => void,
) => {
  React.useEffect(() => {
    const anchorNode = anchorElement.current;

    if (!anchorNode || !open) return;

    anchorNode.addEventListener('click', onDismiss);

    return () => anchorNode.removeEventListener('click', onDismiss);
  }, [anchorElement, open, onDismiss]);
};

const Tooltip = ({
  anchorElement,
  open,
  message,
  arrowPlacement = ARROW_PLACEMENT.top,
  onDismiss,
}: IProps) => {
  const arrowRef = React.useRef<HTMLDivElement>(null);
  const descriptionId = React.useId();
  const stableAnchorNode = useStableAnchorNode(anchorElement);
  const {
    refs,
    floatingStyles,
    placement,
    middlewareData,
    isPositioned,
    context,
  } = useFloating({
    open,
    onOpenChange: (nextOpen) => {
      if (!nextOpen) onDismiss();
    },
    placement: arrowPlacement,
    strategy: 'fixed',
    transform: false,
    middleware: [
      offset(ANCHOR_GAP),
      flip({
        fallbackPlacements: [ARROW_PLACEMENT.top, ARROW_PLACEMENT.bottom],
      }),
      shift({ padding: VIEWPORT_MARGIN }),
      arrow({ element: arrowRef, padding: ARROW_MARGIN }),
    ],
    whileElementsMounted: autoUpdate,
    elements: { reference: stableAnchorNode },
  });

  const { getFloatingProps } = useInteractions([useDismiss(context)]);

  useAnchorDescribedBy(anchorElement, descriptionId, open);
  useAnchorPressDismiss(anchorElement, open, onDismiss);

  if (!stableAnchorNode || !open) return null;

  return (
    <div
      ref={refs.setFloating}
      id={descriptionId}
      role="tooltip"
      className={classNames(
        'z-tooltip w-max max-w-[calc(100vw-64px)] break-words rounded-lg bg-secondary p-2 [&_span]:font-sans [&_span]:text-base [&_span]:font-bold [&_span]:text-dark-body',
        isPositioned ? 'visible' : 'invisible',
      )}
      style={floatingStyles}
      {...getFloatingProps({ onClick: onDismiss })}
    >
      <div
        ref={arrowRef}
        data-testid="arrow"
        style={{ left: middlewareData.arrow?.x }}
        className={classNames(
          "invisible absolute h-3.5 w-3.5 before:visible before:absolute before:h-3.5 before:w-3.5 before:bg-secondary before:content-[''] before:[transform:rotate(45deg)]",
          placement === ARROW_PLACEMENT.top
            ? '-bottom-0.5 before:-bottom-0.5'
            : '-top-0.5 before:-top-0.5',
        )}
      />
      {message}
    </div>
  );
};

export default Tooltip;
